import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useApprovePasswordStore, useNotificationStore, usePostRequisitesStore } from '@lib/store';
import { ValidationUtils } from '@shared/utils';
import { RecipientTypeEnum, type GetRequisitesResponse } from '@shared/api';
import { Button, Constants, ErrorMessage, Input, Modal } from '@shared/widgets';

interface IForm {
  beneficiaryBankName: string;
  beneficiaryBankAddress: string;
  bic: string;
  iban: string;
  beneficiaryName: string;
}
const DEFAULT_VALUES: IForm = { beneficiaryBankName: '', beneficiaryBankAddress: '', bic: '', iban: '', beneficiaryName: '' };

interface AddNewRecipientModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipientType: RecipientTypeEnum;
  selectedRecipient?: GetRequisitesResponse;
  onShowSelectRecipientModal: () => void;
}

export const AddNewRecipientModal = (props: AddNewRecipientModalProps) => {
  const postRequisitesStore = usePostRequisitesStore();
  const approvePasswordStore = useApprovePasswordStore();
  const notificationStore = useNotificationStore();

  const [isContinue, setIsContinue] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');

  const { control, handleSubmit, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });

  const submitHandler = async (data: IForm) => {
    const result = await postRequisitesStore.createRequisites({
      BankName: data.beneficiaryBankName,
      BankAddress: data.beneficiaryBankAddress,
      BIC: data.bic,
      IBAN: data.iban,
      OwnerName: data.beneficiaryName,
    });
    if (result) setIsContinue(true);
  };

  const confirmModal = async () => {
    await approvePasswordStore.approvePassword({ Password: password!, RequestId: postRequisitesStore.data?.Id });
    notificationStore.setSuccess(Constants.NEW_RECIPIENT_SUCCESS_MESSAGE);
    props.onShowSelectRecipientModal();
    props.onClose();
    setPassword('');
    setIsContinue(false);
  };

  const handleBackRecipient = () => {
    setIsContinue(false);
    setPassword('');
  };

  const handleBackButtonClick = () => {
    props.onShowSelectRecipientModal();
    props.onClose();
    reset(DEFAULT_VALUES);
  };

  const handleCloseModal = () => {
    reset(DEFAULT_VALUES);
    props.onClose();
  };

  useEffect(() => {
    if (props.isOpen) {
      if (props.recipientType === RecipientTypeEnum.Show && props.selectedRecipient) {
        reset({
          beneficiaryBankName: props.selectedRecipient.BankName,
          beneficiaryBankAddress: props.selectedRecipient.BankAddress,
          bic: props.selectedRecipient.BIC,
          iban: props.selectedRecipient.IBAN,
          beneficiaryName: props.selectedRecipient.OwnerName,
        });
      } else if (props.recipientType === RecipientTypeEnum.Add) {
        reset(DEFAULT_VALUES);
      }
    }
  }, [props.isOpen, props.recipientType, props.selectedRecipient, reset]);

  return (
    <Modal isOpen={props.isOpen} onClose={handleCloseModal} title={props.recipientType === RecipientTypeEnum.Add ? 'New Recipient' : 'Recipient'}>
      {!isContinue ? (
        <form className='w-full' onSubmit={handleSubmit(submitHandler)}>
          <div>
            <div className='mb-[26px] w-full text-center text-base font-semibold text-[#666] uppercase'>Bank Account Details</div>
            <div className='mt-3'>
              <Controller
                name='beneficiaryBankName'
                control={control}
                rules={{ required: 'Please enter Beneficiary Bank Name' }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <div className='mt-3 w-full'>
                    <div className='mb-1.5 text-sm font-semibold text-[#666] uppercase'>Beneficiary Bank Name</div>
                    <Input
                      variant='outlined'
                      rounded
                      className='h-[46px]'
                      placeholder='Enter Beneficiary Bank Name'
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      name='Beneficiary Bank'
                      required
                      error={error?.type}
                      readOnly={props.recipientType === RecipientTypeEnum.Show}
                    />
                    <ErrorMessage text={error?.message} />
                  </div>
                )}
              />
              <Controller
                name='beneficiaryBankAddress'
                control={control}
                rules={{ required: ' Please enter Beneficiary Bank Address' }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <div className='mt-3 w-full'>
                    <div className='mb-1.5 text-sm font-semibold text-[#666] uppercase'>Beneficiary Bank Address</div>
                    <Input
                      variant='outlined'
                      rounded
                      className='h-[46px]'
                      placeholder='Enter Beneficiary Bank Address'
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      required
                      error={error?.type}
                      name='Beneficiary Bank Address'
                      readOnly={props.recipientType === RecipientTypeEnum.Show}
                    />
                    <ErrorMessage text={error?.message} />
                  </div>
                )}
              />
              <Controller
                name='bic'
                control={control}
                rules={{
                  required: 'Please enter BIC',
                  pattern: { value: ValidationUtils.alphanumericRegex, message: 'The BIC field must be alphanumeric.' },
                }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <div className='mt-3 w-full'>
                    <div className='mb-1.5 text-sm font-semibold text-[#666] uppercase'>BIC</div>
                    <Input
                      variant='outlined'
                      rounded
                      className='h-[46px]'
                      placeholder='Enter BIC'
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      required
                      name='BIC'
                      error={error?.type}
                      readOnly={props.recipientType === RecipientTypeEnum.Show}
                    />
                    <ErrorMessage text={error?.message} />
                  </div>
                )}
              />
              <Controller
                name='iban'
                control={control}
                rules={{
                  required: ' Please enter IBAN / Beneficiary Account',
                  pattern: { value: ValidationUtils.alphanumericRegex, message: 'The IBAN field must be alphanumeric.' },
                }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <div className='mt-3 w-full'>
                    <div className='mb-1.5 text-sm font-semibold text-[#666] uppercase'>IBAN / Beneficiary Account</div>
                    <Input
                      variant='outlined'
                      rounded
                      className='h-[46px]'
                      placeholder='Enter IBAN / Beneficiary Account'
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      required
                      error={error?.type}
                      name='IBAN / Beneficiary Account'
                      readOnly={props.recipientType === RecipientTypeEnum.Show}
                    />
                    <ErrorMessage text={error?.message} />
                  </div>
                )}
              />
              <Controller
                name='beneficiaryName'
                control={control}
                rules={{ required: 'Please enter Beneficiary Name' }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <div className='mt-3 w-full'>
                    <div className='mb-1.5 text-sm font-semibold text-[#666] uppercase'>Beneficiary Name</div>
                    <Input
                      variant='outlined'
                      rounded
                      className='h-[46px]'
                      placeholder='Enter Beneficiary Name'
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      required
                      error={error?.type}
                      name='Beneficiary Name'
                      readOnly={props.recipientType === RecipientTypeEnum.Show}
                    />
                    <ErrorMessage text={error?.message} />
                  </div>
                )}
              />
            </div>
          </div>
          <div className='mt-4 flex w-full flex-row items-center justify-between gap-4'>
            <Button fullWidth color='outlined' onClick={handleBackButtonClick}>
              Back
            </Button>
            {props.recipientType === RecipientTypeEnum.Add && (
              <Button fullWidth color='primary' type='submit'>
                Continue
              </Button>
            )}
          </div>
        </form>
      ) : (
        <div className='mt-3 w-full'>
          <div className='w-full'>
            <Input
              variant='outlined'
              rounded
              className='h-[48px]'
              placeholder='Password'
              value={password}
              onChange={setPassword}
              required
              type='password'
              name='Password'
            />
          </div>
          <div className='mt-4 flex w-full flex-row items-center justify-between gap-4'>
            <Button fullWidth color='outlined' onClick={handleBackRecipient}>
              Back
            </Button>
            <Button fullWidth color='primary' disabled={!password} onClick={confirmModal}>
              Confirm
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
